/**
 * CruxLabs API Service
 * Centralized API communication layer with environment-based configuration
 */

// API Configuration from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

// Build full API base URL
export const API_BASE_URL = `${API_URL}/api`;

/**
 * Generic API response type
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * API Error class for handling API-specific errors
 */
/** What POST /admin/models/upload did: a new model, an existing entry filled in, or files replaced. */
export interface AdminUploadResult {
  modelId: number;
  outcome: 'created' | 'completed' | 'replaced';
  logs: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Base fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  const token = localStorage.getItem('authToken');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    // Handle empty responses
    const text = await response.text();
    return text ? JSON.parse(text) : {} as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * Newsletter API
 */
export const newsletterApi = {
  subscribe: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    const formData = new FormData();
    formData.append('email', email);

    return apiFetch('/newsletter/subscribe', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  },
};

/**
 * Auth API
 */
export const authApi = {
  login: async (email: string, password: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    return apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  logout: async (): Promise<void> => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminToken');
  },

  getCurrentUser: async (): Promise<ApiResponse<any>> => {
    return apiFetch('/auth/me');
  },
};

/**
 * Admin User DTO
 */
export interface AdminUser {
  id: number;
  email: string;
  username: string;
  role: string;
}

/**
 * Admin User List Item
 */
export interface AdminUserListItem {
  id: number;
  username: string;
  email: string;
  status: string;
  country: string | null;
  creationDate: string;
  lastConnection: string | null;
}

/**
 * Admin Crag List Item
 */
export interface AdminCragListItem {
  id: number;
  name: string;
  areaName: string | null;
  country: string | null;
  latitude: number;
  longitude: number;
  routesCount: number;
  sectorsCount: number;
  isActive: boolean;
  containBoulders: boolean;
}

/**
 * Admin Route Item (for crag detail)
 */
export interface AdminRouteItem {
  id: number;
  name: string;
  grade: string;
  sectorName: string;
  ascents: number;
  rating: number;
}

/**
 * Admin Crag Translation
 */
export interface AdminCragTranslation {
  locale: string;
  description: string;
}

/**
 * Admin Crag Model Item
 */
export interface AdminCragModelItem {
  id: number;
  name: string;
  routeCount: number;
  status: string;
}

/**
 * Admin Parking Link (itinerary)
 */
export interface AdminParkingLink {
  sectorId: number;
  sectorName: string;
  sectorLatitude: number;
  sectorLongitude: number;
  parkingId: number;
  parkingName: string;
  parkingLatitude: number;
  parkingLongitude: number;
  walkingTime: number;
  distance: number;
  polyline: string;
}

/**
 * Admin Sector Item (for dropdown)
 */
export interface AdminSectorItem {
  id: number;
  name: string;
}

/**
 * Admin Parking Item (for dropdown)
 */
export interface AdminParkingItem {
  id: number;
  name: string;
}

/**
 * Admin Crag Detail
 */
export interface AdminCragDetail {
  id: number;
  name: string;
  areaName: string | null;
  country: string | null;
  latitude: number;
  longitude: number;
  description: string | null;
  coverImage: string | null;
  isActive: boolean;
  isBoulder: boolean;
  containBoulders: boolean;
  routesCount: number;
  sectorsCount: number;
  routes: AdminRouteItem[];
  translations: AdminCragTranslation[];
  models: AdminCragModelItem[];
  parkingLinks: AdminParkingLink[];
}

/**
 * Admin Crag Detail Response
 */
export interface AdminCragDetailResponse {
  success: boolean;
  data?: AdminCragDetail;
  message?: string;
}

/**
 * Model lifecycle checklist item
 */
export interface AdminModelLifecycleItem {
  key: string;
  label: string;
  done: boolean;
}

/**
 * Model lifecycle progress info
 */
export interface AdminModelLifecycle {
  completedTasks: number;
  totalTasks: number;
  lifecycleStage: string;
  checklistItems: AdminModelLifecycleItem[];
}

/**
 * Model lifecycle stage metadata — single source of truth for labels and badge variants.
 * Mirrors the Kotlin ModelLifecycleStage enum server-side.
 */
export const MODEL_LIFECYCLE_STAGES = {
  uploaded:  { label: 'Uploaded',  variant: 'outline'   },
  assigned:  { label: 'Assigned',  variant: 'secondary' },
  in_review: { label: 'In Review', variant: 'warning'   },
  labelled:  { label: 'Labelled',  variant: 'info'      },
  ready:     { label: 'Ready',     variant: 'success'   },
  published: { label: 'Published', variant: 'success'   },
} as const satisfies Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' }>;

export type ModelLifecycleStageKey = keyof typeof MODEL_LIFECYCLE_STAGES;

export const getLifecycleLabel = (stage: string): string =>
  MODEL_LIFECYCLE_STAGES[stage as ModelLifecycleStageKey]?.label ?? stage;

export const getLifecycleVariant = (stage: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' =>
  MODEL_LIFECYCLE_STAGES[stage as ModelLifecycleStageKey]?.variant ?? 'outline';

/**
 * Production tracking for the photogrammetry workflow.
 * Each timestamp is null until the step is reached. Only the two fields that
 * have no derivable source live here: Labeled is read from the approved edit,
 * Published is read from status='active' (see V5 migration).
 */
export interface AdminModelTracking {
  builtAt: string | null;
  cleanedAt: string | null;
  notes: string | null;
  glbVersion: string | null;
  jsonVersion: string | null;
}

/**
 * Admin Model List Item
 */
export interface AdminModelListItem {
  id: number;
  name: string;
  routeCount: number;
  status: string;
  cragName: string | null;
  lifecycle: AdminModelLifecycle;
  tracking: AdminModelTracking;
}

/**
 * Compact summary of the mobile scan session that seeded a model. Exposed on
 * the admin model detail so we can audit the link between sensor capture (lat
 * / lng / marker_id) and the photogrammetry inputs (Drive folder).
 */
export interface AdminScanSessionSummary {
  id: number;
  capturedAt: string | null;
  latitude: number;
  longitude: number;
  markerId: number | null;
}

/**
 * Admin Model Detail
 */
export interface AdminModelDetail {
  id: number;
  name: string;
  status: string;
  routeCount: number;
  coverImage: string | null;
  cragId: number | null;
  cragName: string | null;
  routes: AdminRouteItem[];
  sectors: AdminSectorItem[];
  lifecycle: AdminModelLifecycle;
  tracking: AdminModelTracking;
  // Provenance — drone-photo Drive folder + originating scan session. Both are
  // nullable on legacy models that predate the contributor pipeline.
  driveFolderId: string | null;
  driveFolderUrl: string | null;
  expectedFileCount: number | null;
  scanSession: AdminScanSessionSummary | null;
}

/**
 * Patch payload for production tracking. Each *Done flag stamps or clears the
 * corresponding timestamp server-side using now(); null fields are left
 * untouched so the caller can update one step at a time.
 */
export interface UpdateModelTrackingPayload {
  builtDone?: boolean;
  cleanedDone?: boolean;
  notes?: string;
  glbVersion?: string;
  jsonVersion?: string;
}

/**
 * Admin Model Detail Response
 */
export interface AdminModelDetailResponse {
  success: boolean;
  data?: AdminModelDetail;
  message?: string;
}

/**
 * Admin Labelling Edit Item (list view)
 */
export interface AdminLabellingEditItem {
  id: number;
  modelId: number;
  modelName: string;
  userId: number;
  username: string;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

/**
 * Admin Labelling Edit Detail (with full JSON)
 */
export interface AdminLabellingEditDetail {
  id: number;
  modelId: number;
  modelName: string;
  userId: number;
  username: string;
  editJson: string;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

/**
 * Admin Labelling Edit Detail Response
 */
export interface AdminLabellingEditDetailResponse {
  success: boolean;
  data?: AdminLabellingEditDetail;
  message?: string;
}

// ============================================
// Scan Session Types
// ============================================

export interface AdminScanSessionItem {
  id: number;
  userId: number;
  username: string;
  latitude: number;
  longitude: number;
  readingsCount: number;
  createdAt: string;
}

export interface AdminScanReading {
  index: number;
  timestamp: number;
  azimuth: number;
  pitch: number;
  roll: number;
  latitude: number;
  longitude: number;
}

export interface AdminScanSessionDetail {
  id: number;
  userId: number;
  username: string;
  sessionTimestamp: string;
  latitude: number;
  longitude: number;
  createdAt: string;
  readings: AdminScanReading[];
}

export interface AdminScanSessionDetailResponse {
  success: boolean;
  data?: AdminScanSessionDetail;
  message?: string;
}

// ============================================
// Info Annotation Types
// ============================================

export interface AdminInfoAnnotationItem {
  id: number;
  modelId: number;
  modelName: string;
  userId: number;
  username: string;
  subcategory: string;
  severity: string | null;
  affectedType: string;
  affectedId: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  isValidated: boolean;
  isActive: boolean;
  confirmationsCount: number;
  outdatedReportsCount: number;
  createdAt: string;
}

export interface AdminInfoAnnotationDetail {
  id: number;
  modelId: number;
  modelName: string;
  userId: number;
  username: string;
  subcategory: string;
  severity: string | null;
  affectedType: string;
  affectedId: number;
  affectedName: string | null;
  durationEnd: string | null;
  seasonalStart: string | null;
  seasonalEnd: string | null;
  positionX: number;
  positionY: number;
  positionZ: number;
  note: string | null;
  isValidated: boolean;
  isActive: boolean;
  confirmationsCount: number;
  outdatedReportsCount: number;
  createdAt: string;
}

export interface AdminInfoAnnotationDetailResponse {
  success: boolean;
  data?: AdminInfoAnnotationDetail;
  message?: string;
}

/**
 * Admin List Response
 */
export interface AdminListResponse<T> {
  success: boolean;
  message?: string;
  data?: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Admin Dashboard Stats
 */
export interface AdminStats {
  totalUsers: number;
  totalCrags: number;
  activeCrags: number;
  inactiveCrags: number;
  totalModels: number;
  totalRoutes: number;
  modelsUploaded: number;
  modelsAssigned: number;
  modelsInReview: number;
  modelsLabelled: number;
  modelsReady: number;
  modelsPublished: number;
}

export interface AdminStatsResponse {
  success: boolean;
  data?: AdminStats;
  message?: string;
}

/**
 * Admin Login Response
 */
export interface AdminLoginResponse {
  success: boolean;
  message: string;
  data?: {
    token: string; // This is now the CSRF token, not JWT
    user: AdminUser;
  };
}

/**
 * Helper to get CSRF token from cookie
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/admin_csrf=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Admin API fetch wrapper with credentials and CSRF
 */
async function adminFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add CSRF token for state-changing requests
  const csrfToken = getCsrfToken();
  if (csrfToken && options.method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method)) {
    (headers as Record<string, string>)['X-CSRF-Token'] = csrfToken;
  }

  const config: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // Send cookies with request
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {} as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * Admin API - Secure cookie-based authentication
 */
export const adminApi = {
  /**
   * Login to admin panel
   * Server sets HttpOnly cookie with JWT
   */
  login: async (username: string, password: string): Promise<AdminLoginResponse> => {
    const response = await adminFetch<AdminLoginResponse>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    // Store user info (not the token - it's in HttpOnly cookie)
    if (response.success && response.data?.user) {
      sessionStorage.setItem('adminUser', JSON.stringify(response.data.user));
    }

    return response;
  },

  /**
   * Verify current admin session (cookie-based)
   */
  verify: async (): Promise<ApiResponse<AdminUser>> => {
    return adminFetch('/admin/verify');
  },

  /**
   * Logout from admin panel
   * Server clears HttpOnly cookie
   */
  logout: async (): Promise<void> => {
    try {
      await adminFetch('/admin/logout', { method: 'POST' });
    } catch (e) {
      // Ignore errors on logout
    }
    sessionStorage.removeItem('adminUser');
  },

  /**
   * Check if user has admin session
   * Note: This checks for CSRF cookie, actual auth is verified by server
   */
  isAuthenticated: (): boolean => {
    return getCsrfToken() !== null;
  },

  /**
   * Get stored admin user from session
   */
  getStoredUser: (): AdminUser | null => {
    const userStr = sessionStorage.getItem('adminUser');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Get CSRF token (for debugging)
   */
  getCsrfToken,

  getStats: async (): Promise<AdminStatsResponse> => {
    return adminFetch('/admin/stats');
  },

  getUsers: async (page: number = 1, search?: string): Promise<AdminListResponse<AdminUserListItem>> => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    if (search) params.set('search', search);
    return adminFetch(`/admin/users?${params.toString()}`);
  },

  getModels: async (page: number = 1, search?: string): Promise<AdminListResponse<AdminModelListItem>> => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    if (search) params.set('search', search);
    return adminFetch(`/admin/models?${params.toString()}`);
  },

  getModelById: async (id: number): Promise<AdminModelDetailResponse> => {
    return adminFetch(`/admin/models/${id}`);
  },

  updateModelStatus: async (modelId: number, status: string): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/models/${modelId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  updateModelTracking: async (
    modelId: number,
    payload: UpdateModelTrackingPayload
  ): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/models/${modelId}/tracking`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  uploadModelCover: async (modelId: number, file: File): Promise<ApiResponse<{ coverImage: string }>> => {
    const url = `${API_BASE_URL}/admin/models/${modelId}/cover`;

    const formData = new FormData();
    formData.append('coverImage', file);

    const headers: HeadersInit = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {} as ApiResponse<{ coverImage: string }>;
  },

  getCrags: async (
    page: number = 1,
    search?: string,
    isActive?: boolean | null
  ): Promise<AdminListResponse<AdminCragListItem>> => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    if (search) params.set('search', search);
    if (isActive !== null && isActive !== undefined) params.set('isActive', isActive.toString());
    return adminFetch(`/admin/crags?${params.toString()}`);
  },

  getCragById: async (id: number): Promise<AdminCragDetailResponse> => {
    return adminFetch(`/admin/crags/${id}`);
  },

  updateCragDescription: async (
    cragId: number,
    locale: string | null,
    description: string
  ): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/crags/${cragId}/description`, {
      method: 'PUT',
      body: JSON.stringify({ locale, description }),
    });
  },

  updateCragStatus: async (cragId: number, isActive: boolean): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/crags/${cragId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
  },

  uploadCragCover: async (cragId: number, file: File): Promise<ApiResponse<{ coverImage: string }>> => {
    const url = `${API_BASE_URL}/admin/crags/${cragId}/cover`;

    const formData = new FormData();
    formData.append('coverImage', file);

    const headers: HeadersInit = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {} as ApiResponse<{ coverImage: string }>;
  },

  getSectors: async (cragId: number): Promise<AdminListResponse<AdminSectorItem>> => {
    return adminFetch(`/admin/crags/${cragId}/sectors`);
  },

  getParkings: async (): Promise<AdminListResponse<AdminParkingItem>> => {
    return adminFetch('/admin/parkings');
  },

  createParkingLink: async (
    cragId: number,
    sectorId: number,
    parkingId: number,
    gpxFile: File
  ): Promise<ApiResponse<void>> => {
    const url = `${API_BASE_URL}/admin/crags/${cragId}/parking-link`;

    const formData = new FormData();
    formData.append('sectorId', sectorId.toString());
    formData.append('parkingId', parkingId.toString());
    formData.append('gpxFile', gpxFile);

    const headers: HeadersInit = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {} as ApiResponse<void>;
  },

  uploadModel: async (
    configFile: File,
    modelLow: File,
    modelMedium: File,
    modelHigh: File,
    replace: boolean = false
  ): Promise<ApiResponse<AdminUploadResult>> => {
    // replace=true overwrites the 3D files of a model that already has some;
    // without it the server answers 409 for such a model.
    const url = `${API_BASE_URL}/admin/models/upload${replace ? '?replace=true' : ''}`;

    const formData = new FormData();
    formData.append('infos', configFile);
    formData.append('model_low', modelLow);
    formData.append('model_medium', modelMedium);
    formData.append('model_high', modelHigh);

    const headers: HeadersInit = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    const text = await response.text();
    return text ? JSON.parse(text) : {} as ApiResponse<AdminUploadResult>;
  },

  // ============================================
  // Labelling Moderation Methods
  // ============================================

  getLabellingEdits: async (
    page: number = 1,
    status?: string
  ): Promise<AdminListResponse<AdminLabellingEditItem>> => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    if (status) params.set('status', status);
    return adminFetch(`/admin/labelling?${params.toString()}`);
  },

  getLabellingEditById: async (id: number): Promise<AdminLabellingEditDetailResponse> => {
    return adminFetch(`/admin/labelling/${id}`);
  },

  approveEdit: async (editId: number): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/labelling/${editId}/approve`, {
      method: 'PUT',
    });
  },

  rejectEdit: async (editId: number, notes: string): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/labelling/${editId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  },

  // ============================================
  // Scan Sessions Methods
  // ============================================

  getScanSessions: async (
    page: number = 1
  ): Promise<AdminListResponse<AdminScanSessionItem>> => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    return adminFetch(`/admin/scans?${params.toString()}`);
  },

  getScanSessionById: async (id: number): Promise<AdminScanSessionDetailResponse> => {
    return adminFetch(`/admin/scans/${id}`);
  },

  // ============================================
  // Info Annotations Methods
  // ============================================

  getInfoAnnotations: async (
    page: number = 1,
    validated?: boolean,
    active?: boolean
  ): Promise<AdminListResponse<AdminInfoAnnotationItem>> => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    if (validated !== undefined) params.set('validated', validated.toString());
    if (active !== undefined) params.set('active', active.toString());
    return adminFetch(`/admin/info-annotations?${params.toString()}`);
  },

  getInfoAnnotationById: async (id: number): Promise<AdminInfoAnnotationDetailResponse> => {
    return adminFetch(`/admin/info-annotations/${id}`);
  },

  validateAnnotation: async (annotationId: number, note?: string): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/info-annotations/${annotationId}/validate`, {
      method: 'PUT',
      body: JSON.stringify({ note: note || null }),
    });
  },

  hideAnnotation: async (annotationId: number, note?: string): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/info-annotations/${annotationId}/hide`, {
      method: 'PUT',
      body: JSON.stringify({ note: note || null }),
    });
  },

  deleteAnnotation: async (annotationId: number): Promise<ApiResponse<void>> => {
    return adminFetch(`/admin/info-annotations/${annotationId}`, {
      method: 'DELETE',
    });
  },

  getAnnotationsByModelId: async (modelId: number): Promise<AdminListResponse<AdminInfoAnnotationItem>> => {
    return adminFetch(`/admin/info-annotations?modelId=${modelId}`);
  },
};

/**
 * Models API
 */
export const modelsApi = {
  getAll: async (page: number = 1): Promise<ApiResponse<any[]>> => {
    return apiFetch(`/models?page=${page}`);
  },

  getById: async (id: string): Promise<ApiResponse<any>> => {
    return apiFetch(`/models/${id}`);
  },
};

/**
 * Crags API
 */
export const cragsApi = {
  getAll: async (page: number = 1): Promise<ApiResponse<any[]>> => {
    return apiFetch(`/crags?page=${page}`);
  },

  getByName: async (name: string): Promise<ApiResponse<any>> => {
    return apiFetch(`/crags/${encodeURIComponent(name)}`);
  },
};

/**
 * Contact API
 */
export const contactApi = {
  submit: async (data: { name: string; email: string; message: string }): Promise<ApiResponse<{ message: string }>> => {
    return apiFetch('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Export configuration for use in components
export const config = {
  apiUrl: API_URL,
  apiBaseUrl: API_BASE_URL,
  contactEmail: process.env.REACT_APP_CONTACT_EMAIL || 'contact@cruxlabs.app',
  googlePlayUrl: process.env.REACT_APP_GOOGLE_PLAY_URL || '#',
  appStoreUrl: process.env.REACT_APP_APP_STORE_URL || '#',
  enableAdmin: process.env.REACT_APP_ENABLE_ADMIN === 'true',
  enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
};

export default {
  newsletter: newsletterApi,
  auth: authApi,
  admin: adminApi,
  models: modelsApi,
  crags: cragsApi,
  contact: contactApi,
  config,
};
